/**
 * Environment Configuration Validator
 * Ensures sensitive data is not exposed in production builds
 */

export class EnvironmentValidator {
    /**
     * Validates environment configuration for security issues
     */
    static validate(env: any): void {
        if (!env.production) {
            return; // Skip validation in development
        }

        // Check for common security issues
        const issues: string[] = [];

        // Check for exposed API keys
        if (this.hasExposedSecrets(env)) {
            issues.push('Potentially exposed API keys or secrets detected');
        }

        // Check for localhost URLs in production
        if (this.hasLocalhostUrls(env)) {
            issues.push('Localhost URLs detected in production environment');
        }

        // Check for HTTP URLs (should be HTTPS)
        if (this.hasInsecureUrls(env)) {
            issues.push('Insecure HTTP URLs detected (should use HTTPS)');
        }

        // Check for debug flags enabled
        if (env.debug === true || env.debugMode === true) {
            issues.push('Debug mode enabled in production');
        }

        if (issues.length > 0) {
            console.error('⚠️  Environment Security Issues Detected:');
            issues.forEach(issue => console.error(`  - ${issue}`));
            
            if (env.production) {
                console.error('❌ Production build contains security issues!');
            }
        } else {
            // console.log('✅ Environment configuration validated successfully');
        }
    }

    private static hasExposedSecrets(env: any): boolean {
        const secretPatterns = [
            /api[_-]?key/i,
            /secret/i,
            /password/i,
            /token/i
        ];

        const envString = JSON.stringify(env);
        return secretPatterns.some(pattern => 
            pattern.test(envString) && envString.includes('=')
        );
    }

    private static hasLocalhostUrls(env: any): boolean {
        const envString = JSON.stringify(env);
        return /localhost|127\.0\.0\.1|0\.0\.0\.0/.test(envString);
    }

    private static hasInsecureUrls(env: any): boolean {
        const envString = JSON.stringify(env);
        // Check for http:// but not https://
        const httpMatches = envString.match(/http:\/\//g);
        const httpsMatches = envString.match(/https:\/\//g);
        
        // If we have http:// but not matching https://, it's insecure
        return httpMatches !== null && httpMatches.length > 0 && 
               (httpsMatches === null || httpMatches.length > httpsMatches.length);
    }

    /**
     * Sanitizes environment for logging (removes sensitive data)
     */
    static sanitizeForLogging(env: any): any {
        const sanitized = { ...env };
        const sensitiveKeys = ['apiKey', 'secret', 'password', 'token'];

        sensitiveKeys.forEach(key => {
            if (key in sanitized) {
                sanitized[key] = '***REDACTED***';
            }
        });

        return sanitized;
    }
}
